import axios from 'axios'
import React, {useEffect, useMemo, useState} from 'react'
import {Modal} from 'react-bootstrap'
import LoadingOverlay from 'react-loading-overlay'
import {AdminDataTable, AdminTableAction, AdminTableActions, AdminTableSearch} from '../../components/AdminDataTable/AdminDataTable'

import './ListasPrecios.css'

const emptyForm = {nombre: '', descripcion: '', porcentajeAumento: ''}

function ListasPrecios() {
  const API_URL = process.env.REACT_APP_API_URL
  const [listas, setListas] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingList, setEditingList] = useState(null)
  const [form, setForm] = useState(emptyForm)
  const [message, setMessage] = useState(null)
  const [formError, setFormError] = useState('')

  const loadLists = async () => {
    setLoading(true)
    try {
      const response = await axios.get(`${API_URL}/listas-precios`)
      setListas(Array.isArray(response.data) ? response.data : [])
    } catch (error) {
      setMessage({type: 'danger', text: error.response?.data?.message || 'No fue posible consultar las listas de precios.'})
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadLists()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const filteredLists = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('es-MX')
    if (!term) return listas
    return listas.filter((list) =>
      `${list.nombre} ${list.descripcion || ''} ${list.porcentajeAumento}`
        .toLocaleLowerCase('es-MX')
        .includes(term)
    )
  }, [listas, search])

  const openCreate = () => {
    setEditingList(null)
    setForm(emptyForm)
    setFormError('')
    setShowModal(true)
  }

  const openEdit = (list) => {
    setEditingList(list)
    setForm({
      nombre: list.nombre,
      descripcion: list.descripcion || '',
      porcentajeAumento: String(list.porcentajeAumento),
    })
    setFormError('')
    setShowModal(true)
  }

  const validate = () => {
    if (!form.nombre.trim()) return 'El nombre es obligatorio.'
    if (form.nombre.trim().length > 100) return 'El nombre no puede exceder 100 caracteres.'
    if (form.descripcion.trim().length > 500) return 'La descripción no puede exceder 500 caracteres.'
    if (form.porcentajeAumento === '' || Number.isNaN(Number(form.porcentajeAumento)) || Number(form.porcentajeAumento) < 0) {
      return 'El porcentaje debe ser un número mayor o igual a cero.'
    }
    if (editingList?.esDefault && (form.nombre !== 'Lista 1' || Number(form.porcentajeAumento) !== 0)) {
      return 'Lista 1 debe conservar su nombre y un aumento de 0%.'
    }
    return null
  }

  const saveList = async (event) => {
    event.preventDefault()
    const validationError = validate()
    if (validationError) {
      setFormError(validationError)
      return
    }

    setLoading(true)
    setFormError('')
    try {
      const payload = {
        nombre: form.nombre.trim(),
        descripcion: form.descripcion.trim(),
        porcentajeAumento: Number(form.porcentajeAumento),
      }
      if (editingList) {
        await axios.put(`${API_URL}/listas-precios/${editingList.id}`, payload)
      } else {
        await axios.post(`${API_URL}/listas-precios`, payload)
      }
      await loadLists()
      setShowModal(false)
      setMessage({type: 'success', text: editingList ? 'Lista actualizada correctamente.' : 'Lista creada correctamente.'})
    } catch (error) {
      setFormError(error.response?.data?.message || 'No fue posible guardar la lista de precios.')
    } finally {
      setLoading(false)
    }
  }

  const deleteList = async (list) => {
    if (list.esDefault || !window.confirm(`¿Deseas eliminar ${list.nombre}?`)) return
    setLoading(true)
    try {
      await axios.delete(`${API_URL}/listas-precios/${list.id}`)
      await loadLists()
      setMessage({type: 'success', text: 'Lista eliminada correctamente.'})
    } catch (error) {
      setMessage({type: 'danger', text: error.response?.data?.message || 'No fue posible eliminar la lista de precios.'})
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      id: 'nombre',
      name: 'Nombre',
      selector: (list) => list.nombre,
      sortable: true,
      cell: (list) => (
        <div>
          <span className='price-list-name'>{list.nombre}</span>
          {list.esDefault && <span className='badge badge-light-success ms-2'>Default</span>}
        </div>
      ),
    },
    {
      id: 'descripcion',
      name: 'Descripción',
      selector: (list) => list.descripcion || '',
      sortable: true,
      wrap: true,
      grow: 2,
      cell: (list) => list.descripcion || '—',
    },
    {
      id: 'porcentajeAumento',
      name: 'Valor a aumentar',
      selector: (list) => Number(list.porcentajeAumento),
      sortable: true,
      right: true,
      cell: (list) => <strong>{Number(list.porcentajeAumento).toLocaleString('es-MX', {maximumFractionDigits: 4})}%</strong>,
    },
    {
      id: 'acciones',
      name: 'Acciones',
      sortable: false,
      right: true,
      width: '130px',
      cell: (list) => (
        <AdminTableActions>
          <AdminTableAction variant='edit' title='Editar' icon='fas fa-pen' onClick={() => openEdit(list)} />
          {!list.esDefault && (
            <AdminTableAction variant='delete' title='Eliminar' icon='fas fa-trash' onClick={() => deleteList(list)} />
          )}
        </AdminTableActions>
      ),
    },
  ]

  const searchControl = <AdminTableSearch value={search} onChange={(event) => setSearch(event.target.value)} ariaLabel='Buscar listas de precios' />

  return (
    <LoadingOverlay active={loading} spinner text='Cargando...'>
      <div className='price-lists-page'>
        {message && (
          <div className={`alert alert-${message.type} alert-dismissible`} role='status'>
            {message.text}
            <button type='button' className='btn-close' aria-label='Cerrar' onClick={() => setMessage(null)}></button>
          </div>
        )}

        <div className='card card-custom shadow admin-list-card price-lists-card'>
          <div className='card-header price-lists-header'>
            <div className='price-lists-heading'>
              <h1>Listas de precios</h1>
              <p>
                Las listas de precios tienen la regla configurada que siempre es un porcentaje de AUMENTO sobre la lista de precios default (Lista 1).<br />
                Los descuentos se gestionan de manera individual para cada cliente.
              </p>
            </div>
            <button type='button' className='btn btn-primary price-lists-add-button' onClick={openCreate}>
              <i className='fas fa-plus'></i> Agregar lista
            </button>
          </div>

          <div className='card-body'>
            {searchControl}
            <AdminDataTable
                columns={columns}
                data={filteredLists}
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                noDataComponent='No se encontraron listas de precios.'
            />
          </div>
        </div>

        <Modal show={showModal} onHide={() => setShowModal(false)} centered dialogClassName='price-list-modal'>
          <form onSubmit={saveList}>
            <Modal.Header closeButton>
              <Modal.Title>{editingList ? 'Editar lista' : 'Agregar lista'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {formError && <div className='alert alert-danger'>{formError}</div>}
              <div className='mb-5'>
                <label htmlFor='price-list-name' className='form-label'>Nombre</label>
                <input id='price-list-name' className='form-control' maxLength='100' required disabled={Boolean(editingList?.esDefault)} value={form.nombre} onChange={(event) => setForm({...form, nombre: event.target.value})} placeholder='Lista 5' />
              </div>
              <div className='mb-5'>
                <label htmlFor='price-list-description' className='form-label'>Descripción</label>
                <textarea id='price-list-description' className='form-control' maxLength='500' rows='4' value={form.descripcion} onChange={(event) => setForm({...form, descripcion: event.target.value})} placeholder='Lista 1 + 7.5% de aumento.'></textarea>
              </div>
              <div>
                <label htmlFor='price-list-percentage' className='form-label'>Porcentaje de aumento</label>
                <div className='input-group'>
                  <input id='price-list-percentage' className='form-control' type='number' min='0' max='9999.9999' step='0.0001' required disabled={Boolean(editingList?.esDefault)} value={form.porcentajeAumento} onChange={(event) => setForm({...form, porcentajeAumento: event.target.value})} placeholder='7.5' />
                  <span className='input-group-text'>%</span>
                </div>
                {editingList?.esDefault && <div className='form-text'>Lista 1 siempre conserva un aumento de 0%.</div>}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button type='button' className='btn btn-secondary' onClick={() => setShowModal(false)}>Cancelar</button>
              <button type='submit' className='btn btn-primary' disabled={loading}>Guardar</button>
            </Modal.Footer>
          </form>
        </Modal>
      </div>
    </LoadingOverlay>
  )
}

export {ListasPrecios}
